import React, { useEffect, useRef } from "react";
import { PageRenderer, RendererRuntimeProvider, type All8PageContent } from "@apa/core-renderers";
import { createRoot } from "react-dom/client";
import { LoadingSpinner } from "../LoadingSpinner";
import { createRuntimeImageComponent } from "../../Helper/storage";
import { MOBILE_PREVIEW_WIDTH } from "./hrefInputUtils";

const runtimeImageComponent = createRuntimeImageComponent();
const runtimeValue = { imageComponent: runtimeImageComponent };

export const MobilePreviewFrame: React.FC<{
	components: All8PageContent;
	loading: boolean;
	error: string | null;
	width?: number;
	height?: number;
}> = ({ components, loading, error, width = MOBILE_PREVIEW_WIDTH, height = 760 }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const rootRef = useRef<ReturnType<typeof createRoot> | null>(null);

	useEffect(() => {
		const iframe = iframeRef.current;
		if (!iframe) return;
		const doc = iframe.contentDocument;
		if (!doc) return;

		doc.open();
		doc.write("<!doctype html><html><head></head><body><div id='preview-root'></div></body></html>");
		doc.close();

		document.querySelectorAll("link[rel='stylesheet'], style").forEach((node) => {
			doc.head.appendChild(node.cloneNode(true));
		});

		const mountNode = doc.getElementById("preview-root");
		if (!mountNode) return;

		const root = createRoot(mountNode);
		rootRef.current = root;
		root.render(
			<RendererRuntimeProvider value={runtimeValue}>
				<div className="page-editor-preview-inner">
					<PageRenderer components={components} />
				</div>
			</RendererRuntimeProvider>
		);

		return () => {
			root.unmount();
		};
	}, [components]);

	if (loading) {
		return (
			<div className="flex h-[760px] items-center justify-center">
				<LoadingSpinner />
			</div>
		);
	}

	if (error) {
		return <div className="p-4 text-xs text-rose-600">{error}</div>;
	}

	if (!components.length) {
		return <div className="p-4 text-xs text-slate-500">Select a site page to see the live preview.</div>;
	}

	return (
		<div className="mx-auto" style={{ width: `${width}px` }}>
			<iframe
				ref={iframeRef}
				title="Link preview"
				style={{ width: "100%", height: `${height}px`, border: "0", background: "white" }}
				className="block mx-auto"
			/>
		</div>
	);
};
